import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Calendar,
  CheckCircle2,
  Clock,
  ExternalLink,
  FileText,
  Loader2,
  MessageSquare,
  Sparkles,
  Users,
} from 'lucide-react';
import { Modal, Select, Button, Badge } from '../../Components/ui';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../api/useApi';
import { useToast } from '../../Components/Feedback';

const formatarDataBr = (dataIso) => {
  if (!dataIso) return '';
  const [ano, mes, dia] = dataIso.split('-');
  return `${dia}/${mes}/${ano}`;
};

export default function PlanejamentoGradeModal({
  isOpen,
  onClose,
  dataAtual,
  idProfessor,
  disciplinaInicial = '',
}) {
  const { institutionId } = useInstitution();
  const { token } = useAuth();
  const navigate = useNavigate();
  const api = useApi();
  const toast = useToast();

  const [disciplinas, setDisciplinas] = useState([]);
  const [disciplinaSelecionada, setDisciplinaSelecionada] = useState(disciplinaInicial);
  const [carregandoDisciplinas, setCarregandoDisciplinas] = useState(false);

  const [planejamentoSemana, setPlanejamentoSemana] = useState(null);
  const [semanaInfo, setSemanaInfo] = useState(null);
  const [carregandoPlano, setCarregandoPlano] = useState(false);

  // Campos de edição rápida
  const [novoConteudo, setNovoConteudo] = useState('');
  const [novasObservacoes, setNovasObservacoes] = useState('');
  const [salvando, setSalvando] = useState(false);

  // 1. Carregar disciplinas do educador
  useEffect(() => {
    if (!isOpen || !institutionId || !token) return;

    const carregarDisciplinas = async () => {
      setCarregandoDisciplinas(true);
      try {
        const url = idProfessor
          ? `/api/planejamento/disciplinas?id_professor=${idProfessor}`
          : `/api/planejamento/disciplinas`;
        const res = await api.get(url);
        const lista = res?.data ?? (Array.isArray(res) ? res : []);
        setDisciplinas(lista);
        if (lista.length > 0) {
          setDisciplinaSelecionada((prev) => {
            if (disciplinaInicial && lista.some((d) => d.nome.toLowerCase() === disciplinaInicial.toLowerCase())) {
              return disciplinaInicial;
            }
            const aindaExiste = lista.some((d) => d.nome === prev);
            return aindaExiste ? prev : lista[0].nome;
          });
        } else {
          setDisciplinaSelecionada('');
        }
      } catch (err) {
        console.error('Erro ao carregar disciplinas no modal da grade:', err);
      } finally {
        setCarregandoDisciplinas(false);
      }
    };

    carregarDisciplinas();
  }, [isOpen, institutionId, token, idProfessor, disciplinaInicial]); // eslint-disable-line react-hooks/exhaustive-deps

  // 2. Carregar planejamento da semana atual para a disciplina selecionada
  useEffect(() => {
    if (!isOpen || !institutionId || !token || !disciplinaSelecionada) {
      setPlanejamentoSemana(null);
      setSemanaInfo(null);
      return;
    }

    const carregarPlano = async () => {
      setCarregandoPlano(true);
      try {
        const dataConsulta = dataAtual || new Date().toISOString().split('T')[0];
        const res = await api.get(
          `/api/planejamento/semana-atual?data=${dataConsulta}&nome_disciplina=${encodeURIComponent(disciplinaSelecionada)}${idProfessor ? `&id_professor=${idProfessor}` : ''}`,
        );
        const data = res?.data ?? res;
        if (data?.planejado && data?.planejamento) {
          setPlanejamentoSemana(data.planejamento);
          setNovasObservacoes(data.planejamento.observacoes || '');
        } else {
          setPlanejamentoSemana(null);
          setNovoConteudo('');
          setNovasObservacoes('');
        }
        setSemanaInfo(data?.semana || null);
      } catch (err) {
        console.error('Erro ao consultar planejamento semanal na grade:', err);
      } finally {
        setCarregandoPlano(false);
      }
    };

    carregarPlano();
  }, [isOpen, institutionId, token, disciplinaSelecionada, dataAtual]); // eslint-disable-line react-hooks/exhaustive-deps

  const infoDisciplina = disciplinas.find((d) => d.nome === disciplinaSelecionada);

  // Marcar status Concluído / Pendente
  const handleToggleStatus = async () => {
    if (!planejamentoSemana) return;
    const novoStatus = planejamentoSemana.status === 'concluido' ? 'pendente' : 'concluido';
    setSalvando(true);
    try {
      const res = await api.patch(`/api/planejamento/${planejamentoSemana.id}`, {
        status: novoStatus,
      });
      const atualizado = res?.data ?? res;
      setPlanejamentoSemana(atualizado);
      toast.success(
        novoStatus === 'concluido'
          ? 'Planejamento da semana marcado como feito!'
          : 'Planejamento reaberto como pendente.',
      );
    } catch (err) {
      toast.error(err.message || 'Erro ao alterar status.');
    } finally {
      setSalvando(false);
    }
  };

  // Salvar anotações / pós-aula
  const handleSalvarObservacoes = async () => {
    if (!planejamentoSemana) return;
    setSalvando(true);
    try {
      const res = await api.patch(`/api/planejamento/${planejamentoSemana.id}`, {
        observacoes: novasObservacoes,
      });
      const atualizado = res?.data ?? res;
      setPlanejamentoSemana(atualizado);
      toast.success('Anotações da semana salvas com sucesso!');
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar anotações.');
    } finally {
      setSalvando(false);
    }
  };

  // Cadastrar planejamento da semana se não existir ainda
  const handleCriarPlanejamento = async (e) => {
    e?.preventDefault();
    if (!novoConteudo.trim()) {
      toast.error('Preencha o conteúdo planejado.');
      return;
    }
    setSalvando(true);
    try {
      const res = await api.post('/api/planejamento', {
        nome_disciplina: disciplinaSelecionada,
        data: dataAtual || new Date().toISOString().split('T')[0],
        conteudo: novoConteudo,
        observacoes: novasObservacoes || null,
        status: 'pendente',
        ...(idProfessor && { id_professor: idProfessor }),
      });
      const criado = res?.data ?? res;
      setPlanejamentoSemana(criado);
      toast.success('Planejamento semanal registrado com sucesso!');
    } catch (err) {
      toast.error(err.message || 'Erro ao registrar planejamento.');
    } finally {
      setSalvando(false);
    }
  };

  const irParaTelaCompleta = () => {
    onClose();
    navigate('/planejamento');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Planejamento Semanal do Educador"
      maxWidth="lg"
    >
      <div className="p-4 sm:p-6 space-y-4">
        {/* Seletor de Disciplina & Info da Semana */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-gray-100 dark:border-gray-800">
          <div className="w-full sm:w-72">
            <Select
              label="Disciplina"
              icon={BookOpen}
              value={disciplinaSelecionada}
              onChange={(e) => setDisciplinaSelecionada(e.target.value)}
              disabled={carregandoDisciplinas || disciplinas.length === 0}
              options={
                disciplinas.length > 0
                  ? disciplinas.map((d) => ({
                      value: d.nome,
                      label: d.eh_compartilhado ? `${d.nome} (Compartilhado)` : d.nome,
                    }))
                  : [{ value: '', label: 'Nenhuma disciplina disponível' }]
              }
            />
          </div>

          {semanaInfo && (
            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 self-start sm:self-end mb-1">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span>
                Semana {semanaInfo.numero_semana} • {formatarDataBr(semanaInfo.data_inicio_semana)} a{' '}
                {formatarDataBr(semanaInfo.data_fim_semana)}
              </span>
            </div>
          )}
        </div>

        {/* Aviso de disciplina compartilhada (Teoria) */}
        {infoDisciplina?.eh_compartilhado && (
          <div className="p-2.5 bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-xl flex items-center gap-2 text-xs text-purple-800 dark:text-purple-300">
            <Users className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
            <span>
              <strong>Teoria Compartilhada:</strong> O conteúdo abaixo é compartilhado com todos os professores que lecionam esta disciplina.
            </span>
          </div>
        )}

        {/* Conteúdo do Planejamento */}
        {carregandoPlano ? (
          <div className="py-10 text-center text-gray-500 dark:text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600 dark:text-blue-400 mb-2" />
            Carregando planejamento da semana...
          </div>
        ) : planejamentoSemana ? (
          <div className="space-y-4">
            {/* Status e Ação Rápida de Concluir */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                  Status do Planejamento:
                </span>
                <Badge tone={planejamentoSemana.status === 'concluido' ? 'success' : 'warning'}>
                  {planejamentoSemana.status === 'concluido' ? (
                    <span className="inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Concluído (Feito)
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Pendente
                    </span>
                  )}
                </Badge>
              </div>

              <Button
                variant={planejamentoSemana.status === 'concluido' ? 'secondary' : 'success'}
                size="sm"
                icon={planejamentoSemana.status === 'concluido' ? Clock : CheckCircle2}
                onClick={handleToggleStatus}
                disabled={salvando}
              >
                {planejamentoSemana.status === 'concluido' ? 'Reabrir' : 'Marcar como Feito'}
              </Button>
            </div>

            {/* Conteúdo Planejado */}
            <div className="space-y-1">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" /> Conteúdo Planejado para Esta Semana
              </div>
              <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed bg-white dark:bg-gray-800 p-3.5 rounded-xl border border-gray-200 dark:border-gray-700">
                {planejamentoSemana.conteudo}
              </div>
            </div>

            {/* Anotações / Pós-aula */}
            <div className="space-y-1.5">
              <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" /> Anotações / O que foi realizado
              </div>
              <textarea
                rows={3}
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3.5 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Adicione observações da aula (ex: turma avançou bem, faltou terminar página 10)..."
                value={novasObservacoes}
                onChange={(e) => setNovasObservacoes(e.target.value)}
              />
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={handleSalvarObservacoes}
                  disabled={salvando}
                  icon={salvando ? Loader2 : CheckCircle2}
                >
                  Salvar Anotações
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 rounded-2xl space-y-3">
            <div className="flex items-start gap-2">
              <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                  Nenhum planejamento registrado para esta semana
                </h4>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                  Digite o conteúdo planejado para a semana de{' '}
                  {formatarDataBr(semanaInfo?.data_inicio_semana || dataAtual)}:
                </p>
              </div>
            </div>

            <form onSubmit={handleCriarPlanejamento} className="space-y-3 pt-1">
              <textarea
                rows={4}
                required
                className="w-full rounded-xl border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Teoria e leitura na clave de sol, compasso 4/4, páginas 12 a 15..."
                value={novoConteudo}
                onChange={(e) => setNovoConteudo(e.target.value)}
              />

              <div className="flex items-center justify-end gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={salvando}
                  icon={salvando ? Loader2 : CheckCircle2}
                >
                  {salvando ? 'Salvando...' : 'Salvar Planejamento da Semana'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Rodapé do Modal com Link para tela completa */}
        <div className="pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <button
            type="button"
            onClick={irParaTelaCompleta}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-medium"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Abrir histórico e visão completa de Planejamento
          </button>

          <Button variant="secondary" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

